import { useEffect, useRef } from "react";
import { blobState } from "../three/blobState";

const EATEN_WORD = "Models.";

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const ease = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;

// The headline sits in a sticky stage while the section scrolls past. Scroll progress
// drives the blob in from the right, across "Models." (eating it letter by letter) and
// then down off the screen. Scrolling back up plays it in reverse.
export const Hero = () => {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const lettersRef = useRef([]);
  const fallbackRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const letters = lettersRef.current;
    let base = [];
    let eatenBefore = 0;
    let frame = 0;

    // Letter centres relative to the stage, measured without our transforms applied.
    const measure = () => {
      letters.forEach((el) => {
        el.style.transform = "";
        el.style.opacity = "";
      });
      const stage = stageRef.current.getBoundingClientRect();
      base = letters.map((el) => {
        const r = el.getBoundingClientRect();
        return { x: r.left - stage.left + r.width / 2, y: r.top - stage.top + r.height / 2, h: r.height };
      });
    };

    const update = () => {
      frame = 0;
      const section = sectionRef.current;
      const stageEl = stageRef.current;
      if (!section || !stageEl || !base.length) return;

      const rect = section.getBoundingClientRect();
      const stage = stageEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const p = clamp(-rect.top / Math.max(rect.height - vh, 1));

      const first = base[0];
      const last = base[base.length - 1];
      const lineY = stage.top + first.y;
      const radius0 = first.h * 0.55;
      const peekX = vw + radius0 * 0.3;
      const readyX = stage.left + last.x + radius0 * 1.15;
      const endX = stage.left + first.x - radius0 * 0.1;

      let x = peekX;
      let y = lineY;
      let drop = 0;
      if (p >= 0.12 && p < 0.32) x = lerp(peekX, readyX, ease((p - 0.12) / 0.2));
      else if (p >= 0.32 && p < 0.78) x = lerp(readyX, endX, (p - 0.32) / 0.46);
      else if (p >= 0.78) {
        drop = ease((p - 0.78) / 0.22);
        x = endX - drop * vw * 0.04;
        y = lerp(lineY, vh + radius0 * 2.5, drop); // fall fully out of view by the end
      }

      const radius = radius0 * (1 + 0.05 * eatenBefore) * (1 - 0.2 * drop);
      let eaten = 0;
      let nextLetter = null;
      letters.forEach((el, i) => {
        const lx = stage.left + base[i].x;
        const ly = stage.top + base[i].y;
        const k = clamp((lx + radius - x) / radius);
        if (k >= 0.95) eaten += 1;
        else if (!nextLetter || lx > nextLetter.x) nextLetter = { x: lx, y: ly };
        if (k <= 0) {
          el.style.transform = "";
          el.style.opacity = "";
          return;
        }
        el.style.transform = `translate(${(x - lx) * k}px, ${(y - ly) * k}px) scale(${1 - 0.85 * k}) rotate(${-30 * k}deg)`;
        el.style.opacity = String(1 - ease(clamp((k - 0.55) / 0.45)));
      });
      if (eaten > eatenBefore) blobState.gulp = 1;
      eatenBefore = eaten;

      // Eyes follow the next letter, or look down once everything is eaten.
      if (nextLetter && drop === 0) {
        const dx = nextLetter.x - x;
        const dy = nextLetter.y - y;
        const length = Math.hypot(dx, dy) || 1;
        blobState.lookX = dx / length;
        blobState.lookY = -dy / length;
      } else {
        blobState.lookX = -0.3;
        blobState.lookY = -0.9;
      }

      blobState.x = x;
      blobState.y = y;
      blobState.radius = radius;
      // Hidden once the hero has finished, so it never lingers over the next section.
      blobState.visible = p < 1 && y - radius < vh;

      // Without WebGL, move a CSS blob instead.
      const fallback = fallbackRef.current;
      if (fallback && !document.documentElement.classList.contains("webgl")) {
        fallback.style.display = blobState.visible ? "block" : "none";
        fallback.style.width = fallback.style.height = `${radius * 2}px`;
        fallback.style.transform = `translate(${x - radius}px, ${y - radius}px)`;
      }
    };

    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const remeasure = () => {
      measure();
      update();
    };

    remeasure();
    document.fonts?.ready.then(remeasure);
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", remeasure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", remeasure);
      blobState.visible = false;
    };
  }, []);

  return (
    <section className="hero" id="top" ref={sectionRef}>
      <div className="hero-stage" ref={stageRef}>
        <h1 className="hero-title">
          <span className="visually-hidden">Khushi Tulsiyan, software engineer. </span>
          <span className="hero-line">Clean Code.</span>
          <span className="hero-line">
            Smart{" "}
            <span className="hero-word">
              {EATEN_WORD.split("").map((char, index) => (
                <span key={index} className="hero-letter" ref={(el) => (lettersRef.current[index] = el)}>
                  {char}
                </span>
              ))}
            </span>
          </span>
        </h1>
        <p className="hero-sub">Khushi Tulsiyan · Software Engineer</p>
        <span className="scroll-hint" aria-hidden="true">Scroll</span>
      </div>
      <div className="blob-fallback" ref={fallbackRef} aria-hidden="true" />
    </section>
  );
};
